import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api, BACKEND_URL, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, FileText, Send, CheckCircle2, XCircle, PauseCircle, PlayCircle, Users, ClipboardList, ScrollText, MessageSquare, UserCog, Trash2 } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import { sanitizeHtml } from "@/lib/sanitize";

// Resolve the correct <img src> for a stored logo. Legacy data-URLs stay as-is;
// object-storage URLs (`/api/files/...`) get the backend origin prepended so the
// browser hits the API host and not the frontend origin.
const logoSrc = (u) => {
  if (!u) return "";
  if (u.startsWith("data:") || /^https?:/i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

export default function SocietyDetail() {
  const { id } = useParams();
  const [tab, setTab] = useState("overview");
  const [society, setSociety] = useState(null);
  const [stats, setStats] = useState(null);

  const load = async () => {
    const [s, st] = await Promise.all([
      api.get(`/admin/societies/${id}`),
      api.get(`/tenants/${id}/committee/stats`).catch(() => ({ data: null })),
    ]);
    setSociety(s.data);
    setStats(st.data);
  };
  useEffect(() => { load().catch((e) => toast.error(apiErr(e))); /* eslint-disable-next-line */ }, [id]);

  const toggleSub = async (status) => {
    try {
      await api.patch(`/admin/societies/${id}`, { subscription_status: status });
      toast.success(`Status set to ${status}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!society) return <AdminLayout title="Loading…"><div className="text-stone-500">Fetching society…</div></AdminLayout>;

  return (
    <AdminLayout
      subtitle={society.slug}
      title={society.name}
      actions={
        <div className="flex items-center gap-2">
          <Link to={`/portal/${society.slug}/login`} target="_blank" rel="noreferrer"
            className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm hover:bg-sand-100">Member portal ↗</Link>
          {society.subscription_status === "Active" ? (
            <button onClick={() => toggleSub("Suspended")} className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-4 py-2 text-sm hover:bg-sand-100" data-testid="btn-suspend"><PauseCircle size={14}/>Suspend</button>
          ) : (
            <button onClick={() => toggleSub("Active")} className="inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm" data-testid="btn-activate"><PlayCircle size={14}/>Activate</button>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap gap-2 mb-8 border-b border-stone-200">
        {[
          { k: "overview", l: "Overview", icon: FileText },
          { k: "settings", l: "Settings", icon: UserCog },
          { k: "bulk", l: "Bulk Upload", icon: Upload },
          { k: "units", l: "Units & Owners", icon: Users },
          { k: "invoices", l: "Invoicing", icon: FileText },
          { k: "notices", l: "Notices", icon: ScrollText },
          { k: "complaints", l: "Complaints", icon: MessageSquare },
          { k: "applications", l: "Applications", icon: ClipboardList },
          { k: "committee", l: "Committee", icon: UserCog },
          { k: "audit", l: "Audit log", icon: FileText },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? "border-terracotta text-ink" : "border-transparent text-stone-500 hover:text-ink"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview society={society} stats={stats} reload={load} />}
      {tab === "settings" && <SettingsTab society={society} reload={load} />}
      {tab === "bulk" && <BulkUploadTab tenantId={id} onDone={load} />}
      {tab === "units" && <UnitsTab tenantId={id} />}
      {tab === "invoices" && <InvoicesTab tenantId={id} society={society} />}
      {tab === "notices" && <NoticesTab tenantId={id} />}
      {tab === "complaints" && <ComplaintsTab tenantId={id} />}
      {tab === "applications" && <ApplicationsTab tenantId={id} />}
      {tab === "committee" && <CommitteeTab tenantId={id} />}
      {tab === "audit" && <AuditTab tenantId={id} />}
    </AdminLayout>
  );
}

function Overview({ society, stats, reload }) {
  const [payAmt, setPayAmt] = useState("");
  const [uploading, setUploading] = useState(false);
  const recordPayment = async () => {
    try {
      await api.post(`/admin/societies/${society.id}/subscription-payment`, {
        amount: Number(payAmt || 0), payment_date: new Date().toISOString().slice(0, 10),
        mode: "Manual", reference: "", extend_months: 12,
      });
      toast.success("Subscription extended by 12 months");
      setPayAmt("");
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    if (!/\.(png|jpe?g|svg)$/i.test(file.name)) { toast.error("PNG, JPG or SVG only"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/societies/${society.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo updated — will appear on new invoice & notice PDFs");
      reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };
  const removeLogo = async () => {
    if (!window.confirm("Remove the current logo? The letterhead will render without a logo until you upload a new one.")) return;
    try { await api.patch(`/admin/societies/${society.id}`, { logo_url: "" }); toast.success("Logo removed"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Panel title="Society details">
        <KV k="Registration" v={society.registration_number || "—"}/>
        <KV k="Act" v={society.registration_act || "—"}/>
        <KV k="PAN" v={society.pan || "—"}/>
        <KV k="GST" v={society.gst_applicable ? `${society.gstin || "—"} · ${society.gst_rate}%` : "Not applicable"}/>
        <KV k="Contact" v={`${society.contact_phone || "—"} · ${society.contact_email || "—"}`}/>
        <KV k="Address" v={society.address || "—"}/>
      </Panel>
      <Panel title="Society logo">
        <div className="flex items-start gap-4">
          <div className="w-24 h-24 rounded-xl border border-stone-200 bg-sand-100 flex items-center justify-center overflow-hidden shrink-0">
            {society.logo_url ? (
              <img src={logoSrc(society.logo_url)} alt="logo" className="max-w-full max-h-full object-contain" data-testid="society-logo-preview"/>
            ) : (
              <span className="text-xs text-stone-400">No logo</span>
            )}
          </div>
          <div className="flex-1 text-sm text-stone-600">
            <p className="text-xs text-stone-500 mb-3">Appears on invoice and notice letterheads. Recommended 400×400 px, transparent PNG. Max 2 MB. PNG / JPG / SVG.</p>
            <label className="inline-flex items-center gap-1.5 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-4 py-2 text-xs font-medium cursor-pointer" data-testid="logo-upload-btn">
              {uploading ? "Uploading…" : (society.logo_url ? "Replace logo" : "Upload logo")}
              <input type="file" accept="image/png,image/jpeg,image/svg+xml" hidden onChange={(e) => uploadLogo(e.target.files?.[0])}/>
            </label>
            {society.logo_url && (
              <button onClick={removeLogo} className="ml-2 text-xs text-stone-500 hover:text-terracotta" data-testid="logo-remove-btn">Remove</button>
            )}
          </div>
        </div>
      </Panel>
      <Panel title="Subscription">
        <KV k="Status" v={<StatusBadge status={society.subscription_status}/>}/>
        <KV k="Start" v={society.subscription_start_date}/>
        <KV k="End" v={society.subscription_end_date}/>
        <KV k="Unit count" v={society.unit_count_at_signup}/>
        <div className="mt-4 pt-4 border-t border-stone-200">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2">Record payment (extends 12m)</div>
          <div className="flex gap-2">
            <input value={payAmt} onChange={(e) => setPayAmt(e.target.value)} placeholder="Amount ₹" data-testid="sub-pay-amount"
              className="flex-1 rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-terracotta"/>
            <button onClick={recordPayment} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-4 py-2 text-sm" data-testid="sub-pay-submit">Record</button>
          </div>
        </div>
      </Panel>
      <Panel title="Committee snapshot">
        <KV k="Total units" v={stats?.total_units ?? "—"}/>
        <KV k="Occupied by tenants" v={stats?.occupied_by_tenant ?? "—"}/>
        <KV k="Draft invoices" v={stats?.draft_invoices ?? "—"}/>
        <KV k="Open complaints" v={stats?.open_complaints ?? "—"}/>
        <KV k="Pending applications" v={stats?.pending_applications ?? "—"}/>
        <KV k="Pending ownership changes" v={stats?.pending_ownership ?? "—"}/>
      </Panel>
      <Panel title="Bank accounts" className="lg:col-span-3">
        {(society.bank_accounts || []).length === 0 ? (
          <div className="text-stone-500 text-sm">No bank accounts configured.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {society.bank_accounts.map((b, i) => (
              <div key={i} className="rounded-xl border border-stone-200 bg-sand-50 p-4">
                <div className="text-xs uppercase tracking-widest text-terracotta font-semibold">{b.purpose}</div>
                <div className="editorial text-lg mt-1">{b.bank_name}</div>
                <div className="text-sm text-stone-600">A/c {b.account_number} · IFSC {b.ifsc}</div>
                <div className="text-sm text-stone-600">{b.holder_name}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}


function SettingsTab({ society, reload }) {
  return (
    <div className="space-y-6" data-testid="settings-tab">
      <IdentitySection society={society} reload={reload}/>
      <StatutorySection society={society} reload={reload}/>
      <BankAccountsSection society={society} reload={reload}/>
      <GSTSection society={society} reload={reload}/>
      <LogoSection society={society} reload={reload}/>
    </div>
  );
}

function SettingsCard({ title, desc, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{title}</div>
        {desc && <div className="text-xs text-stone-500 mt-1">{desc}</div>}
      </div>
      {children}
    </div>
  );
}

function Fld({ label, value, onChange, type = "text", testid, span }) {
  return (
    <div className={span ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"/>
    </div>
  );
}

function useSectionSave(society, reload) {
  const [busy, setBusy] = useState(false);
  const save = async (patch) => {
    setBusy(true);
    try { await api.patch(`/admin/societies/${society.id}`, patch); toast.success("Saved"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return [busy, save];
}

function IdentitySection({ society, reload }) {
  const [f, setF] = useState({
    name: society.name || "", registration_number: society.registration_number || "",
    registration_act: society.registration_act || "", pan: society.pan || "",
    address: society.address || "", contact_phone: society.contact_phone || "", contact_email: society.contact_email || "",
  });
  const [busy, save] = useSectionSave(society, reload);
  return (
    <SettingsCard title="Society Identity" desc="Legal name, registration and contact details. Appear on all invoice & notice letterheads.">
      <div className="grid md:grid-cols-2 gap-4">
        <Fld label="Legal name" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="set-name"/>
        <Fld label="PAN" value={f.pan} onChange={(v) => setF({ ...f, pan: v })} testid="set-pan"/>
        <Fld label="Registration number" value={f.registration_number} onChange={(v) => setF({ ...f, registration_number: v })} testid="set-regno"/>
        <Fld label="Registration act" value={f.registration_act} onChange={(v) => setF({ ...f, registration_act: v })} testid="set-regact"/>
        <Fld label="Contact phone" value={f.contact_phone} onChange={(v) => setF({ ...f, contact_phone: v })} testid="set-phone"/>
        <Fld label="Contact email" value={f.contact_email} onChange={(v) => setF({ ...f, contact_email: v })} testid="set-email"/>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Registered address</label>
          <textarea rows="2" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })}
            data-testid="set-address"
            className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"/>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <button disabled={busy} onClick={() => save(f)} data-testid="set-identity-save"
          className="rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-6 py-2 text-sm font-medium">{busy ? "Saving…" : "Save identity"}</button>
      </div>
    </SettingsCard>
  );
}

function StatutorySection({ society, reload }) {
  const [rows, setRows] = useState(society.statutory_numbers || []);
  const [busy, save] = useSectionSave(society, reload);
  const add = () => setRows((r) => [...r, { label: "", value: "" }]);
  const upd = (i, k, v) => setRows((r) => r.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  return (
    <SettingsCard title="Other Statutory Numbers" desc="Add any additional statutory registrations (Society Reg Cert No., Shop & Establishment, etc.). Displayed on official documents where relevant.">
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid md:grid-cols-[1fr_2fr_auto] gap-2 items-center" data-testid={`stat-row-${i}`}>
            <input value={r.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Label (e.g., SRC No.)" className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-terracotta"/>
            <input value={r.value} onChange={(e) => upd(i, "value", e.target.value)} placeholder="Value" className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-terracotta"/>
            <button onClick={() => del(i)} className="p-2 text-stone-400 hover:text-terracotta" aria-label="Remove"><Trash2 size={14}/></button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-stone-500 italic">None added yet.</div>}
      </div>
      <div className="flex items-center justify-between mt-4">
        <button onClick={add} data-testid="stat-add" className="text-sm text-terracotta">+ Add statutory number</button>
        <button disabled={busy} onClick={() => save({ statutory_numbers: rows.filter((r) => r.label || r.value) })}
          data-testid="set-statutory-save"
          className="rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-6 py-2 text-sm font-medium">{busy ? "Saving…" : "Save numbers"}</button>
      </div>
    </SettingsCard>
  );
}

function BankAccountsSection({ society, reload }) {
  const [rows, setRows] = useState(society.bank_accounts || []);
  const [busy, save] = useSectionSave(society, reload);
  const add = () => setRows((r) => [...r, { bank_name: "", branch: "", account_number: "", ifsc: "", holder_name: "", purpose: "General" }]);
  const upd = (i, k, v) => setRows((r) => r.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => {
    if (!window.confirm("Delete this bank account?\n\nPosted invoices that already reference it will keep their existing bank details (historical PDFs are unaffected). Only new invoices will use the remaining accounts.")) return;
    setRows((r) => r.filter((_, idx) => idx !== i));
  };
  return (
    <SettingsCard title="Bank Accounts" desc="Add, edit or remove bank accounts. If only one is present, it's used for all invoice purposes. Purpose tag Maintenance / Sinking / General / Other.">
      <div className="space-y-3">
        {rows.map((b, i) => (
          <div key={i} className="grid md:grid-cols-6 gap-2 items-end bg-sand-100 border border-stone-200 rounded-xl p-3" data-testid={`bank-row-${i}`}>
            <Fld label="Bank name" value={b.bank_name} onChange={(v) => upd(i, "bank_name", v)} testid={`bank-name-${i}`}/>
            <Fld label="Branch" value={b.branch} onChange={(v) => upd(i, "branch", v)}/>
            <Fld label="A/c number" value={b.account_number} onChange={(v) => upd(i, "account_number", v)} testid={`bank-acc-${i}`}/>
            <Fld label="IFSC" value={b.ifsc} onChange={(v) => upd(i, "ifsc", v)} testid={`bank-ifsc-${i}`}/>
            <Fld label="Holder name" value={b.holder_name} onChange={(v) => upd(i, "holder_name", v)}/>
            <div className="flex items-end gap-1">
              <div className="flex-1">
                <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Purpose</label>
                <select value={b.purpose} onChange={(e) => upd(i, "purpose", e.target.value)} data-testid={`bank-purpose-${i}`}
                  className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-2 py-2.5 text-sm outline-none focus:border-terracotta">
                  {["Maintenance", "Sinking", "General", "Other"].map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <button onClick={() => del(i)} className="p-2 text-stone-400 hover:text-terracotta" data-testid={`bank-del-${i}`} aria-label="Delete"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-stone-500 italic bg-sand-50 border border-dashed border-stone-300 rounded-xl p-4 text-center">No bank accounts yet — click below to add the first one.</div>}
      </div>
      <div className="flex items-center justify-between mt-4">
        <button onClick={add} data-testid="bank-add" className="text-sm text-terracotta">+ Add bank account</button>
        <button disabled={busy} onClick={() => save({ bank_accounts: rows.filter((b) => b.bank_name && b.account_number) })}
          data-testid="set-banks-save"
          className="rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-6 py-2 text-sm font-medium">{busy ? "Saving…" : "Save bank accounts"}</button>
      </div>
    </SettingsCard>
  );
}

function GSTSection({ society, reload }) {
  const [f, setF] = useState({
    gst_applicable: !!society.gst_applicable,
    gstin: society.gstin || "", gst_rate: society.gst_rate ?? 18, hsn_sac: society.hsn_sac || "",
  });
  const [busy, save] = useSectionSave(society, reload);
  const onToggle = (v) => {
    if (!v && society.gst_applicable) {
      if (!window.confirm("Turn GST OFF? New invoices will be generated without tax breakup. Already-Posted invoices keep their existing GST treatment.")) return;
    }
    setF({ ...f, gst_applicable: v });
  };
  return (
    <SettingsCard title="GST" desc="Toggle GST applicability at any time. Past posted invoices retain their original GST treatment; only new invoices reflect the current setting.">
      <label className="inline-flex items-center gap-3 cursor-pointer mb-4">
        <input type="checkbox" checked={f.gst_applicable} onChange={(e) => onToggle(e.target.checked)} data-testid="set-gst-toggle" className="w-5 h-5 accent-terracotta"/>
        <span className="text-sm font-medium">GST Applicable</span>
      </label>
      {f.gst_applicable && (
        <div className="grid md:grid-cols-3 gap-4">
          <Fld label="GSTIN" value={f.gstin} onChange={(v) => setF({ ...f, gstin: v })} testid="set-gstin"/>
          <Fld label="Tax rate %" type="number" value={f.gst_rate} onChange={(v) => setF({ ...f, gst_rate: Number(v) })} testid="set-gstrate"/>
          <Fld label="HSN / SAC" value={f.hsn_sac} onChange={(v) => setF({ ...f, hsn_sac: v })} testid="set-hsn"/>
        </div>
      )}
      <div className="flex justify-end mt-4">
        <button disabled={busy} onClick={() => save(f)} data-testid="set-gst-save"
          className="rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-6 py-2 text-sm font-medium">{busy ? "Saving…" : "Save GST"}</button>
      </div>
    </SettingsCard>
  );
}

function LogoSection({ society, reload }) {
  const [uploading, setUploading] = useState(false);
  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    if (!/\.(png|jpe?g|svg)$/i.test(file.name)) { toast.error("PNG, JPG or SVG only"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/societies/${society.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo updated — reflected on new invoice & notice PDFs");
      reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove the current logo?")) return;
    try { await api.patch(`/admin/societies/${society.id}`, { logo_url: "" }); toast.success("Logo removed"); reload(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <SettingsCard title="Society Logo" desc="Recommended 400×400 px, transparent PNG. Max 2 MB. PNG / JPG / SVG.">
      <div className="flex items-start gap-6">
        <div className="w-28 h-28 rounded-xl border border-stone-200 bg-sand-100 flex items-center justify-center overflow-hidden shrink-0">
          {society.logo_url ? <img src={logoSrc(society.logo_url)} alt="logo" className="max-w-full max-h-full object-contain"/> : <span className="text-xs text-stone-400">No logo</span>}
        </div>
        <div className="flex-1 flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-4 py-2 text-xs font-medium cursor-pointer" data-testid="set-logo-upload">
            {uploading ? "Uploading…" : (society.logo_url ? "Replace logo" : "Upload logo")}
            <input type="file" accept="image/png,image/jpeg,image/svg+xml" hidden onChange={(e) => uploadLogo(e.target.files?.[0])}/>
          </label>
          {society.logo_url && <button onClick={remove} className="rounded-full border border-stone-300 hover:bg-sand-100 text-xs px-4 py-2" data-testid="set-logo-remove">Remove</button>}
        </div>
      </div>
    </SettingsCard>
  );
}


function Panel({ title, children, className = "" }) {
  return (
    <div className={`bg-white border border-stone-200 rounded-2xl p-6 ${className}`}>
      <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-4">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}
function KV({ k, v }) { return <div className="flex justify-between gap-4 text-sm"><div className="text-stone-500">{k}</div><div className="text-right font-medium">{v}</div></div>; }
function StatusBadge({ status }) {
  const m = { Active: "bg-emerald-100 text-emerald-800", Suspended: "bg-amber-100 text-amber-800", Expired: "bg-red-100 text-red-800" };
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${m[status] || "bg-stone-100"}`}>{status}</span>;
}

function BulkUploadTab({ tenantId, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = () => window.open(`${BACKEND_URL}/api/tenants/${tenantId}/bulk/template`, "_blank");

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/tenants/${tenantId}/bulk/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/bulk/commit/${preview.preview_id}`);
      toast.success(`Committed: ${data.inserted.units} units, ${data.inserted.owners} owners`);
      setPreview(null);
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm hover:bg-sand-100" data-testid="bulk-template">
          <Download size={16}/> Download template
        </button>
        <label className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-5 py-2.5 text-sm cursor-pointer" data-testid="bulk-upload-btn">
          <Upload size={16}/> Upload Excel
          <input type="file" accept=".xlsx" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
        </label>
      </div>
      <div className="mb-4 text-xs text-stone-500 bg-sand-100 border border-stone-200 rounded-xl px-4 py-3 inline-block">
        💡 You only need to fill in one of <b>AreaSqm</b> or <b>AreaSqft</b> per unit — the other will be calculated automatically (1 sq m = 10.7639 sq ft).
      </div>
      {busy && <div className="text-stone-500 text-sm">Working…</div>}
      {preview && (
        <div className="space-y-6">
          {preview.errors?.length > 0 ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
              <div className="text-red-800 font-semibold mb-2">Preview has errors. Fix and re-upload:</div>
              <ul className="list-disc pl-6 text-sm text-red-700">{preview.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          ) : (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 flex items-center justify-between">
              <div className="text-emerald-900">All rows validated. Ready to import.</div>
              <button onClick={commit} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-2.5 text-sm font-medium" data-testid="bulk-commit">
                Commit import
              </button>
            </div>
          )}
          {["Units", "Owners", "Occupancy", "Vehicles"].map((s) => (
            preview[s]?.count > 0 && (
              <div key={s} className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b border-stone-200 text-xs uppercase tracking-widest text-stone-500 font-semibold">{s} · {preview[s].count} rows</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-sand-50">
                      <tr>{preview[s].headers.map((h, i) => <th key={i} className="px-4 py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview[s].rows.slice(0, 20).map((r, i) => (
                        <tr key={i} className="border-t border-stone-100">
                          {preview[s].headers.map((h, j) => <td key={j} className="px-4 py-2">{String(r[h] ?? "")}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview[s].count > 20 && <div className="p-3 text-xs text-stone-500 text-center">…{preview[s].count - 20} more rows</div>}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

function UnitsTab({ tenantId }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const load = () => api.get(`/tenants/${tenantId}/units`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const filtered = rows.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      String(u.building || "").toLowerCase().includes(s) ||
      String(u.wing || "").toLowerCase().includes(s) ||
      String(u.unit_no || "").toLowerCase().includes(s) ||
      (u.owner_names || []).join(" ").toLowerCase().includes(s) ||
      (u.occupancy?.tenant_name || "").toLowerCase().includes(s)
    );
  });

  const groups = filtered.reduce((acc, u) => {
    const key = u.building || "—";
    (acc[key] ||= []).push(u);
    return acc;
  }, {});
  const buildingKeys = Object.keys(groups).sort();

  const toggle = (b) => setExpanded((prev) => ({ ...prev, [b]: !prev[b] }));
  const isOpen = (b) => (expanded[b] !== undefined ? expanded[b] : true);

  const del = async (u) => {
    if (!window.confirm(`Delete unit ${u.building}${u.wing ? "/" + u.wing : ""}/${u.unit_no}?\n\nThis will remove owner links and any draft invoices tied to this unit. Posted invoices and history are preserved.`)) return;
    try { await api.delete(`/tenants/${tenantId}/units/${u.id}`); toast.success("Unit deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search building, unit, owner, tenant…"
          data-testid="units-search"
          className="flex-1 max-w-md rounded-full border border-stone-300 bg-white px-4 py-2 text-sm outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"/>
        <div className="text-xs text-stone-500">{filtered.length} of {rows.length} units</div>
      </div>
      {buildingKeys.length === 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-500">
          {rows.length === 0 ? "No units yet. Import via Bulk Upload tab." : "No units match your search."}
        </div>
      )}
      <div className="space-y-4">
        {buildingKeys.map((b) => {
          const units = groups[b];
          return (
            <div key={b} className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`bldg-${b}`}>
              <button onClick={() => toggle(b)} className="w-full flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-sand-50 hover:bg-sand-100 transition-colors" data-testid={`bldg-toggle-${b}`}>
                <div className="flex items-baseline gap-3">
                  <div className="editorial text-lg">Building {b}</div>
                  <div className="text-xs text-stone-500">{units.length} unit{units.length === 1 ? "" : "s"}</div>
                </div>
                <span className="text-stone-500 text-sm">{isOpen(b) ? "▾" : "▸"}</span>
              </button>
              {isOpen(b) && (
                <table className="w-full text-left text-sm">
                  <thead className="bg-sand-50/60">
                    <tr>
                      <Th>Unit</Th><Th>Type</Th><Th>Area</Th><Th>Owners</Th><Th>Occupier</Th><Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {units.sort((a, b2) => String(a.wing || "").localeCompare(String(b2.wing || "")) || String(a.unit_no || "").localeCompare(String(b2.unit_no || ""))).map((u) => (
                      <tr key={u.id} className="border-t border-stone-100 hover:bg-sand-50/40" data-testid={`unit-row-${u.id}`}>
                        <Td><div className="font-medium">{u.wing ? `${u.wing} / ` : ""}{u.unit_no}</div><div className="text-xs text-stone-500">Floor {u.floor || "—"}</div></Td>
                        <Td>{u.unit_type}</Td>
                        <Td>{Math.round(u.area_sqft || 0)} sqft<div className="text-xs text-stone-500">{(u.area_sqm || 0).toFixed(1)} sqm</div></Td>
                        <Td>{(u.owner_names || []).join(", ") || <span className="text-stone-400">—</span>}</Td>
                        <Td>{u.occupancy?.occupier_type === "Tenant" ? `Tenant: ${u.occupancy?.tenant_name || "—"}` : "Self"}</Td>
                        <Td><button onClick={() => del(u)} data-testid={`unit-del-${u.id}`} className="text-xs text-stone-400 hover:text-terracotta">Delete</button></Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InvoicesTab({ tenantId }) {
  const [rows, setRows] = useState([]);
  const [showBatch, setShowBatch] = useState(false);
  const [batch, setBatch] = useState({
    filter_building: "", filter_wing: "", period_label: `Annual ${new Date().getFullYear()}`,
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    maintenance_basis: "per_sqft", maintenance_rate: 2, sinking_basis: "per_sqft", sinking_rate: 0.5,
  });
  const load = () => api.get(`/tenants/${tenantId}/invoices`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const createBatch = async () => {
    try {
      const { data } = await api.post(`/tenants/${tenantId}/invoices/batch`, batch);
      toast.success(`Draft batch created (${data.count} invoices)`);
      setShowBatch(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const post = async (id) => { await api.post(`/tenants/${tenantId}/invoices/${id}/post`); toast.success("Posted"); load(); };
  const cancel = async (id) => {
    if (!window.confirm("Delete/void this invoice? Drafts are hard-deleted; Posted invoices are marked Cancelled.")) return;
    await api.delete(`/tenants/${tenantId}/invoices/${id}`); toast.success("Cancelled/Deleted"); load();
  };
  const openPdf = (id) => window.open(`${BACKEND_URL}/api/tenants/${tenantId}/invoices/${id}/pdf`, "_blank");

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={() => setShowBatch(!showBatch)} className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-5 py-2.5 text-sm" data-testid="btn-new-batch">
          <FileText size={16}/> New annual demand batch
        </button>
      </div>
      {showBatch && (
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-4">Batch parameters</div>
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Period label" value={batch.period_label} onChange={(v) => setBatch({ ...batch, period_label: v })}/>
            <Input label="Due date" type="date" value={batch.due_date} onChange={(v) => setBatch({ ...batch, due_date: v })}/>
            <Input label="Filter building (optional)" value={batch.filter_building} onChange={(v) => setBatch({ ...batch, filter_building: v })}/>
            <Select label="Maintenance basis" value={batch.maintenance_basis} onChange={(v) => setBatch({ ...batch, maintenance_basis: v })} options={["per_sqft", "per_sqm", "lumpsum"]}/>
            <Input label="Maintenance rate ₹" type="number" value={batch.maintenance_rate} onChange={(v) => setBatch({ ...batch, maintenance_rate: Number(v) })}/>
            <Input label="Sinking rate ₹" type="number" value={batch.sinking_rate} onChange={(v) => setBatch({ ...batch, sinking_rate: Number(v) })}/>
          </div>
          <div className="flex justify-end mt-4"><button onClick={createBatch} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-6 py-2.5 text-sm" data-testid="btn-create-batch">Generate drafts</button></div>
        </div>
      )}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-sand-50 border-b border-stone-200">
            <tr><Th>Invoice</Th><Th>Unit</Th><Th>Period</Th><Th>Total</Th><Th>Status</Th><Th>Due</Th><Th></Th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-stone-100" data-testid={`invoice-row-${r.id}`}>
                <Td>{r.invoice_no || <span className="text-stone-400">Draft</span>}</Td>
                <Td>{r.unit_label}</Td>
                <Td>{r.period_label}</Td>
                <Td>₹ {r.total?.toLocaleString?.() ?? r.total}</Td>
                <Td><InvoiceStatus s={r.status}/></Td>
                <Td>{r.due_date}</Td>
                <Td>
                  <div className="flex gap-2 justify-end">
                    {r.status === "Draft" && <>
                      <button onClick={() => post(r.id)} className="text-emerald-700 text-xs font-medium inline-flex items-center gap-1" data-testid={`btn-post-${r.id}`}><Send size={12}/> Post</button>
                      <button onClick={() => cancel(r.id)} className="text-stone-500 text-xs">Delete</button>
                    </>}
                    {r.status !== "Draft" && <button onClick={() => openPdf(r.id)} className="text-terracotta text-xs font-medium" data-testid={`btn-pdf-${r.id}`}>PDF ↗</button>}
                  </div>
                </Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-stone-500">No invoices. Generate a demand batch to start.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvoiceStatus({ s }) {
  const m = { Draft: "bg-stone-100 text-stone-700", Posted: "bg-amber-100 text-amber-800", Paid: "bg-emerald-100 text-emerald-800", "Partially Paid": "bg-blue-100 text-blue-800", Cancelled: "bg-red-100 text-red-800" };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${m[s] || "bg-stone-100"}`}>{s}</span>;
}

function NoticesTab({ tenantId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", category: "General", target: "All" });
  const load = () => api.get(`/tenants/${tenantId}/notices`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);
  const create = async () => {
    try {
      await api.post(`/tenants/${tenantId}/notices`, form);
      toast.success("Notice issued");
      setForm({ title: "", body: "", category: "General", target: "All" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openPdf = (id) => window.open(`${BACKEND_URL}/api/tenants/${tenantId}/notices/${id}/pdf`, "_blank");
  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 bg-white border border-stone-200 rounded-2xl p-6">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-4">Draft a notice</div>
        <div className="space-y-3">
          <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="notice-title"/>
          <Select label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={["General", "Meeting", "Minutes", "Tenant", "Broadcast", "Other"]}/>
          <Select label="Audience" value={form.target} onChange={(v) => setForm({ ...form, target: v })} options={["All", "Owners", "Tenants"]}/>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Body</label>
            <div className="mt-2" data-testid="notice-body-editor">
              <RichTextEditor
                value={form.body}
                onChange={(html) => setForm({ ...form, body: html })}
                placeholder="Draft the notice — use headings, lists, bold, links…"
                testid="notice-body"
                minHeight={220}
              />
            </div>
          </div>
          <button onClick={create} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-6 py-3 text-sm font-medium" data-testid="notice-submit">
            Issue notice + generate PDF
          </button>
        </div>
      </div>
      <div className="lg:col-span-3 space-y-3">
        {rows.map((n) => (
          <div key={n.id} className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`notice-${n.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-terracotta font-semibold">{n.category}</div>
                <div className="editorial text-xl mt-1">{n.title}</div>
                <div className="text-xs text-stone-500 mt-1">{n.issued_at?.slice(0, 16)?.replace("T", " ")} · Target: {n.target}</div>
              </div>
              <button onClick={() => openPdf(n.id)} className="text-terracotta text-sm font-medium">PDF ↗</button>
            </div>
            <div className="text-sm text-stone-600 mt-3 line-clamp-4 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(n.body) }} />
          </div>
        ))}
        {rows.length === 0 && <div className="p-10 text-center text-stone-500 bg-white border border-stone-200 rounded-2xl">No notices yet.</div>}
      </div>
    </div>
  );
}

const COMMITTEE_ROLE_KEYS = ["Chairman", "Treasurer", "Secretary", "SocietyManager"];
const COMMITTEE_ROLE_LABELS = { Chairman: "Chairman", Treasurer: "Treasurer", Secretary: "Secretary", SocietyManager: "Society Manager" };

function CommitteeTab({ tenantId }) {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [matrix, setMatrix] = useState(null);

  const load = async () => {
    const [m, mx] = await Promise.all([
      api.get(`/tenants/${tenantId}/committee/members`),
      api.get(`/tenants/${tenantId}/committee/permissions`),
    ]);
    setMembers(m.data);
    setMatrix(mx.data.roles);
  };
  useEffect(() => { load().catch((e) => toast.error(apiErr(e))); /* eslint-disable-next-line */ }, [tenantId]);

  const searchPeople = async (q) => {
    setSearch(q);
    if (!q || q.length < 2) { setCandidates([]); return; }
    const { data } = await api.get(`/tenants/${tenantId}/people?q=${encodeURIComponent(q)}`);
    setCandidates(data.slice(0, 10));
  };

  const assign = async (personId, roles) => {
    try {
      await api.post(`/tenants/${tenantId}/committee/assign`, { person_id: personId, roles });
      toast.success("Committee roles updated");
      setSearch(""); setCandidates([]);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const memberRoleToggle = (person, role) => {
    const current = new Set((person.roles || []).filter((r) => COMMITTEE_ROLE_KEYS.includes(r)));
    if (current.has(role)) current.delete(role); else current.add(role);
    assign(person.id, Array.from(current));
  };

  const removeFromCommittee = (person) => assign(person.id, []);

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Add a committee member</div>
          <input value={search} onChange={(e) => searchPeople(e.target.value)} placeholder="Search resident by name, phone, or email…"
            data-testid="committee-search"
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"/>
          {candidates.length > 0 && (
            <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-stone-100 border border-stone-200 rounded-xl">
              {candidates.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-sand-50" data-testid={`cand-${p.id}`}>
                  <div>
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-stone-500">{p.phone || p.email || "—"}</div>
                    <div className="text-xs text-stone-400 mt-0.5">{(p.roles || []).join(", ") || "—"}</div>
                  </div>
                  <button onClick={() => assign(p.id, ["SocietyManager"])} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-3 py-1.5 text-xs">
                    Add as Manager
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Permission matrix</div>
          {matrix && (
            <ul className="space-y-3 text-sm">
              {COMMITTEE_ROLE_KEYS.map((r) => (
                <li key={r}>
                  <div className="font-medium">{COMMITTEE_ROLE_LABELS[r]}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{(matrix[r] || []).join(" · ") || "—"}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="lg:col-span-3">
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200 text-xs uppercase tracking-widest text-stone-500 font-semibold">Current committee ({members.length})</div>
          {members.length === 0 ? (
            <div className="p-10 text-center text-stone-500">No committee members assigned yet. Search a resident on the left to add.</div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-sand-50">
                <tr>
                  <Th>Member</Th>
                  {COMMITTEE_ROLE_KEYS.map((r) => <Th key={r}><span className="text-center block">{COMMITTEE_ROLE_LABELS[r]}</span></Th>)}
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {members.map((p) => (
                  <tr key={p.id} className="border-t border-stone-100" data-testid={`committee-row-${p.id}`}>
                    <Td>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-stone-500">{p.phone || p.email || "—"}</div>
                    </Td>
                    {COMMITTEE_ROLE_KEYS.map((r) => {
                      const has = (p.roles || []).includes(r);
                      return (
                        <Td key={r} className="text-center">
                          <input type="checkbox" checked={has}
                            data-testid={`role-${p.id}-${r}`}
                            onChange={() => memberRoleToggle(p, r)}
                            className="w-4 h-4 accent-terracotta"/>
                        </Td>
                      );
                    })}
                    <Td className="text-right">
                      <button onClick={() => removeFromCommittee(p)} className="text-xs text-stone-500 hover:text-terracotta" data-testid={`remove-${p.id}`}>Remove</button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ComplaintsTab({ tenantId }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/tenants/${tenantId}/complaints`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);
  const setStatus = async (id, status) => {
    await api.post(`/tenants/${tenantId}/complaints/${id}/status`, { status, remarks: "" });
    load();
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead className="bg-sand-50 border-b border-stone-200"><tr><Th>Subject</Th><Th>Raised by</Th><Th>Status</Th><Th>When</Th><Th></Th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-b border-stone-100" data-testid={`complaint-${c.id}`}>
              <Td><div className="font-medium">{c.subject}</div><div className="text-xs text-stone-500 line-clamp-1">{c.description}</div></Td>
              <Td>{c.raised_by_name}<div className="text-xs text-stone-500">{c.raised_by_role}</div></Td>
              <Td>{c.status}</Td>
              <Td>{c.created_at?.slice(0, 10)}</Td>
              <Td>
                <div className="flex gap-2 justify-end">
                  {c.status !== "In Progress" && <button onClick={() => setStatus(c.id, "In Progress")} className="text-amber-700 text-xs font-medium">Mark In Progress</button>}
                  {c.status !== "Resolved" && <button onClick={() => setStatus(c.id, "Resolved")} className="text-emerald-700 text-xs font-medium" data-testid={`btn-resolve-${c.id}`}>Resolve</button>}
                </div>
              </Td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-stone-500">No complaints raised.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ApplicationsTab({ tenantId }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/tenants/${tenantId}/applications`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);
  const decide = async (id, approve) => {
    await api.post(`/tenants/${tenantId}/applications/${id}/decide`, { approve, remarks: "" });
    load();
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead className="bg-sand-50 border-b border-stone-200"><tr><Th>Type</Th><Th>Applicant</Th><Th>Details</Th><Th>Status</Th><Th></Th></tr></thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-b border-stone-100" data-testid={`app-${a.id}`}>
              <Td>{a.type}</Td>
              <Td>{a.applicant_name}</Td>
              <Td className="max-w-[380px]"><div className="line-clamp-2 text-stone-600">{a.details}</div></Td>
              <Td>{a.status}</Td>
              <Td>
                <div className="flex gap-2 justify-end">
                  {a.status === "Submitted" && <>
                    <button onClick={() => decide(a.id, true)} className="text-emerald-700 text-xs font-medium inline-flex items-center gap-1"><CheckCircle2 size={12}/>Approve</button>
                    <button onClick={() => decide(a.id, false)} className="text-terracotta text-xs font-medium inline-flex items-center gap-1"><XCircle size={12}/>Reject</button>
                  </>}
                </div>
              </Td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-stone-500">No applications submitted.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab({ tenantId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/admin/societies/${tenantId}/audit`).then(({ data }) => setRows(data)); }, [tenantId]);
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead className="bg-sand-50 border-b border-stone-200"><tr><Th>When</Th><Th>Actor</Th><Th>Action</Th><Th>Entity</Th><Th>Remarks</Th></tr></thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-b border-stone-100">
              <Td className="text-stone-500">{a.at?.slice(0, 19)?.replace("T", " ")}</Td>
              <Td>{a.actor_name} <span className="text-xs text-stone-500">({a.actor_role})</span></Td>
              <Td className="font-mono text-xs">{a.action}</Td>
              <Td className="text-xs">{a.entity} · {a.entity_id?.slice(0, 8)}</Td>
              <Td className="text-xs text-stone-500 max-w-[300px] truncate">{a.remarks}</Td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-stone-500">No audit entries yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) { return <th className="text-xs uppercase tracking-wider font-semibold text-stone-500 py-3 px-4">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`py-3 px-4 ${className}`}>{children}</td>; }
function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-base focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none"/>
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-base focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
